import { supabase } from '../lib/supabase';

// ─── List attached entries ─────────────────────────────────────────────────────

export async function listSubcontractorsForProject(projectId) {
  const { data, error } = await supabase
    ?.from('project_intervenants')
    ?.select(`
      id,
      role,
      notes,
      active,
      created_at,
      subcontractor_id,
      subcontractors (
        id,
        company_name,
        trade,
        contact_person,
        contact_email,
        contact_phone,
        address,
        rating
      )
    `)
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[projectDirectoryService] listSubcontractorsForProject error:', error);
    return [];
  }
  return (data || [])?.map((row) => ({
    projectEntryId: row?.id,
    role: row?.role,
    notes: row?.notes,
    active: row?.active,
    createdAt: row?.created_at,
    ...row?.subcontractors,
  }));
}

export async function listProfessionalsForProject(projectId) {
  const { data, error } = await supabase
    ?.from('project_professionals')
    ?.select(`
      id,
      role,
      notes,
      active,
      created_at,
      professional_id,
      professionals (
        id,
        company_name,
        profession,
        contact_person,
        contact_email,
        contact_phone,
        address
      )
    `)
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[projectDirectoryService] listProfessionalsForProject error:', error);
    return [];
  }
  return (data || [])?.map((row) => ({
    projectEntryId: row?.id,
    role: row?.role,
    notes: row?.notes,
    active: row?.active,
    createdAt: row?.created_at,
    ...row?.professionals,
  }));
}

export async function listClientsForProject(projectId) {
  const { data, error } = await supabase
    ?.from('project_clients')
    ?.select(`
      id,
      role,
      notes,
      active,
      created_at,
      client_id,
      clients (
        id,
        company_name,
        contact_person,
        contact_email,
        contact_phone,
        address
      )
    `)
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[projectDirectoryService] listClientsForProject error:', error);
    return [];
  }
  return (data || [])?.map((row) => ({
    projectEntryId: row?.id,
    role: row?.role,
    notes: row?.notes,
    active: row?.active,
    createdAt: row?.created_at,
    ...row?.clients,
  }));
}

// ─── Attach entries ────────────────────────────────────────────────────────────

export async function attachSubcontractor({ projectId, subcontractorId, role, notes }) {
  const { data, error } = await supabase
    ?.from('project_intervenants')
    ?.insert({ project_id: projectId, subcontractor_id: subcontractorId, role, notes: notes || null })
    ?.select()
    ?.single();

  if (error) {
    console.error('[projectDirectoryService] attachSubcontractor error:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function attachProfessional({ projectId, professionalId, role, notes }) {
  const { data, error } = await supabase
    ?.from('project_professionals')
    ?.insert({ project_id: projectId, professional_id: professionalId, role, notes: notes || null })
    ?.select()
    ?.single();

  if (error) {
    console.error('[projectDirectoryService] attachProfessional error:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function attachClient({ projectId, clientId, role, notes }) {
  const { data, error } = await supabase
    ?.from('project_clients')
    ?.insert({ project_id: projectId, client_id: clientId, role, notes: notes || null })
    ?.select()
    ?.single();

  if (error) {
    console.error('[projectDirectoryService] attachClient error:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

// ─── Detach entry ──────────────────────────────────────────────────────────────

export async function detachEntry(type, projectEntryId) {
  const tableMap = {
    subcontractor: 'project_intervenants',
    professional: 'project_professionals',
    client: 'project_clients',
  };
  const table = tableMap?.[type];
  if (!table) return { error: { message: 'Unknown type' } };

  const { error } = await supabase?.from(table)?.delete()?.eq('id', projectEntryId);
  if (error) {
    console.error('[projectDirectoryService] detachEntry error:', error);
    return { error };
  }
  return { error: null };
}

// ─── Update entry ──────────────────────────────────────────────────────────────

export async function updateEntry(type, projectEntryId, { role, notes, active }) {
  const tableMap = {
    subcontractor: 'project_intervenants',
    professional: 'project_professionals',
    client: 'project_clients',
  };
  const table = tableMap?.[type];
  if (!table) return { data: null, error: { message: 'Unknown type' } };

  const updates = {};
  if (role !== undefined) updates.role = role;
  if (notes !== undefined) updates.notes = notes;
  if (active !== undefined) updates.active = active;

  const { data, error } = await supabase
    ?.from(table)
    ?.update(updates)
    ?.eq('id', projectEntryId)
    ?.select()
    ?.single();

  if (error) {
    console.error('[projectDirectoryService] updateEntry error:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

// ─── Available (not yet attached) entries ─────────────────────────────────────

export async function listAvailableSubcontractors(projectId) {
  // Get already-attached subcontractor IDs
  const { data: attached } = await supabase
    ?.from('project_intervenants')
    ?.select('subcontractor_id')
    ?.eq('project_id', projectId);

  const attachedIds = (attached || [])?.map((r) => r?.subcontractor_id);

  let query = supabase
    ?.from('subcontractors')
    ?.select('id, company_name, trade, contact_person')
    ?.order('company_name', { ascending: true });

  if (attachedIds?.length > 0) {
    query = query?.not('id', 'in', `(${attachedIds?.join(',')})`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[projectDirectoryService] listAvailableSubcontractors error:', error);
    return [];
  }
  return data || [];
}

export async function listAvailableProfessionals(projectId) {
  const { data: attached } = await supabase
    ?.from('project_professionals')
    ?.select('professional_id')
    ?.eq('project_id', projectId);

  const attachedIds = (attached || [])?.map((r) => r?.professional_id);

  let query = supabase
    ?.from('professionals')
    ?.select('id, company_name, profession, contact_person')
    ?.order('company_name', { ascending: true });

  if (attachedIds?.length > 0) {
    query = query?.not('id', 'in', `(${attachedIds?.join(',')})`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[projectDirectoryService] listAvailableProfessionals error:', error);
    return [];
  }
  return data || [];
}

export async function listAvailableClients(projectId) {
  const { data: attached } = await supabase
    ?.from('project_clients')
    ?.select('client_id')
    ?.eq('project_id', projectId);

  const attachedIds = (attached || [])?.map((r) => r?.client_id);

  let query = supabase
    ?.from('clients')
    ?.select('id, company_name, contact_person')
    ?.order('company_name', { ascending: true });

  if (attachedIds?.length > 0) {
    query = query?.not('id', 'in', `(${attachedIds?.join(',')})`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[projectDirectoryService] listAvailableClients error:', error);
    return [];
  }
  return data || [];
}

// ─── Tab badge counts (one query per linking table) ────────────────────────────

export async function fetchTabCounts(projectId) {
  const [subRes, profRes, clientRes] = await Promise.all([
    supabase
      ?.from('project_intervenants')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('project_id', projectId),
    supabase
      ?.from('project_professionals')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('project_id', projectId),
    supabase
      ?.from('project_clients')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('project_id', projectId),
  ]);

  return {
    subcontractor: subRes?.count ?? 0,
    professional: profRes?.count ?? 0,
    client: clientRes?.count ?? 0,
  };
}
